import type { SVGProps } from 'react';
import { cx } from './cx';

/**
 * One line-icon set on a 20 box at 1.5 stroke, square caps and joins, so the
 * icons sit with Instrument Sans's straight terminals rather than looking hand-drawn.
 *
 * One mark, one meaning. The rail used to draw `chart` for Home, Cash flow
 * and Outcomes, `users` for Funders and Grantees, `gavel` for Calls, Board
 * and Governance and `document` for Reports, the SED register and the
 * vault, so on a phone's bottom bar, where the icon is most of what there
 * is, three destinations wore the same face. Each destination now has its
 * own, and `settings` is a cog rather than the pencil that meant "edit"
 * everywhere else.
 */
const paths = {
  bell: 'M5 8.5a5 5 0 0 1 10 0v4l1.5 2h-13l1.5-2z M8.5 16.5a1.5 1.5 0 0 0 3 0',
  shield: 'M10 2.5l6 2.5v4.5c0 3.5-2.5 6-6 8-3.5-2-6-4.5-6-8V5z M7.5 10l2 2 3.5-4',
  calendar: 'M3.5 5.5h13v11h-13z M3.5 9h13 M7 3v3 M13 3v3',
  document: 'M5 2.5h6l4 4v11H5z M11 2.5v4h4 M7.5 11h5 M7.5 14h3.5',
  money: 'M10 4.5v11 M12.5 7.5c-.5-.8-1.4-1.2-2.5-1.2-1.4 0-2.3.7-2.3 1.7s.9 1.5 2.3 1.8c1.6.3 2.6.9 2.6 2.1s-1.1 1.9-2.6 1.9c-1.2 0-2.2-.5-2.7-1.4',
  coins: 'M10 3c3.3 0 6 1.1 6 2.4S13.3 7.8 10 7.8 4 6.7 4 5.4 6.7 3 10 3z M4 5.4v4.2c0 1.3 2.7 2.4 6 2.4s6-1.1 6-2.4V5.4 M4 9.6v4.2c0 1.3 2.7 2.4 6 2.4s6-1.1 6-2.4V9.6',
  gavel: 'M11 4l5 5 M9 6l5 5 M10 5l-6 6 2.5 2.5 6-6z M3.5 16.5h7.5',
  chart: 'M3.5 16.5h13 M5.5 13.5v-3 M9 13.5V6.5 M12.5 13.5V8.5 M16 13.5V4',
  plug: 'M10 2.5v3 M6.5 17.5v-2a3.5 3.5 0 0 1-2-3V10h11v2.5a3.5 3.5 0 0 1-2 3v2 M7.5 5.5h5',
  users: 'M3 16.5v-1a3.5 3.5 0 0 1 3.5-3.5h2a3.5 3.5 0 0 1 3.5 3.5v1 M7.5 9.5a3 3 0 1 0 0-6 3 3 0 0 0 0 6z M13.5 12.2a3.5 3.5 0 0 1 3.5 3.3v1 M13 9.2a2.6 2.6 0 1 0 0-5.2',
  check: 'M4 10.5l4 4 8-9',
  alert: 'M10 3.5l7.5 13h-15z M10 8.5v3.5 M10 14v.5',
  clock: 'M10 3a7 7 0 1 1 0 14 7 7 0 0 1 0-14z M10 6v4.5l2.8 1.7',
  search: 'M9 3.5a5.5 5.5 0 1 1 0 11 5.5 5.5 0 0 1 0-11z M13 13l4 4',
  plus: 'M10 4v12 M4 10h12',
  edit: 'M3.5 16.5l3.5-.8L16.5 6.2a1.8 1.8 0 0 0-2.7-2.7L4.3 13z M11.5 5.5l3 3',
  trash: 'M3.5 5.5h13 M7.5 5.5V3.5h5v2 M5.5 5.5l.8 11h7.4l.8-11 M8.3 8.5v5 M11.7 8.5v5',
  download: 'M10 3v9 M6.5 9l3.5 3.5L13.5 9 M3.5 16.5h13',
  print: 'M6 7.5V3.5h8v4 M6 14.5H3.5v-5a1.5 1.5 0 0 1 1.5-1.5h10a1.5 1.5 0 0 1 1.5 1.5v5H14 M6 12h8v5.5H6z',
  external: 'M11.5 3.5h5v5 M16.5 3.5L9 11 M15 11.5v3.5a1.5 1.5 0 0 1-1.5 1.5H5a1.5 1.5 0 0 1-1.5-1.5V6.5A1.5 1.5 0 0 1 5 5h3.5',
  chevron: 'M7.5 4.5l5.5 5.5-5.5 5.5',
  close: 'M5 5l10 10 M15 5L5 15',
  info: 'M10 3a7 7 0 1 1 0 14 7 7 0 0 1 0-14z M10 9v4.5 M10 6.3v.5',
  filter: 'M3.5 4.5h13l-5.2 6v5l-2.6-1.5v-3.5z',
  // Three rules. Used for the phone bar's More control, which used to
  // draw the magnifier the desktop header uses for Search.
  menu: 'M3.5 5.5h13 M3.5 10h13 M3.5 14.5h13',
  // Three squares in a row: what else can be done here. Not the three
  // rules, which are the bar's More, a place to go; the page's and a
  // row's More actions used to wear the same mark on the same screen, so
  // the hamburger beside a title read as a second navigation menu.
  more: 'M4 9.25h1.5v1.5H4z M9.25 9.25h1.5v1.5h-1.5z M14.5 9.25h1.5v1.5H14.5z',
  // Destinations that used to borrow another page's mark.
  home: 'M3 9.5L10 3.5l7 6 M5 8v8.5h10V8 M8.5 16.5v-4.5h3v4.5',
  settings:
    'M8.6 4.6L8.8 2.5h2.4l.2 2.1 1.4.6 1.7-1.3 1.6 1.6-1.3 1.7.6 1.4 2.1.2v2.4l-2.1.2-.6 1.4 1.3 1.7-1.6 1.6-1.7-1.3-1.4.6-.2 2.1H8.8l-.2-2.1-1.4-.6-1.7 1.3-1.6-1.6 1.3-1.7-.6-1.4-2.1-.2V8.8l2.1-.2.6-1.4-1.3-1.7 1.6-1.6 1.7 1.3z M10 7.5a2.5 2.5 0 1 1 0 5 2.5 2.5 0 0 1 0-5z',
  folder: 'M2.5 5h5l1.5 2h8.5v9.5h-15z',
  receipt: 'M5 2.5h10v15l-1.7-1.2-1.6 1.2-1.7-1.2-1.7 1.2-1.6-1.2L5 17.5z M7.5 6.5h5 M7.5 9.5h5 M7.5 12.5h3',
  bank: 'M2.5 7.5L10 3l7.5 4.5z M4.5 9.5v5 M8 9.5v5 M12 9.5v5 M15.5 9.5v5 M2.5 16.5h15',
  target: 'M10 3a7 7 0 1 1 0 14 7 7 0 0 1 0-14z M10 6.5a3.5 3.5 0 1 1 0 7 3.5 3.5 0 0 1 0-7z M10 9.75v.5',
  inbox: 'M2.5 11l2.5-7.5h10l2.5 7.5v5.5h-15z M2.5 11h4.5l1 2h4l1-2h4.5',
  list: 'M7.5 5.5h9 M7.5 10h9 M7.5 14.5h9 M3.75 5.5h.5 M3.75 10h.5 M3.75 14.5h.5',
  gauge: 'M3.5 14a6.5 6.5 0 1 1 13 0 M10 14l3-4 M3.5 16.5h13',
  lock: 'M4.5 9h11v8h-11z M7 9V6.5a3 3 0 0 1 6 0V9 M10 12v2',
  case: 'M3 6.5h14v10H3z M7.5 6.5V4h5v2.5 M3 10.5h14',
  scale: 'M10 3v13.5 M6.5 16.5h7 M4 6h12 M4 6l-2 5h4z M16 6l-2 5h4z',
  // One person, for the office-bearers register: Funders wear `users`.
  person: 'M10 3a3 3 0 1 1 0 6 3 3 0 0 1 0-6z M4.5 17c0-3.1 2.4-5.5 5.5-5.5s5.5 2.4 5.5 5.5',
  // Three columns of unequal height: the pipeline board's stages.
  columns: 'M3 4h4v12H3z M8 4h4v8H8z M13 4h4v10h-4z',
  exit: 'M8.5 3.5h-5v13h5 M12.5 6.5L16 10l-3.5 3.5 M16 10H7.5',
} as const;

export type IconName = keyof typeof paths;

export const ICON_NAMES = Object.keys(paths) as IconName[];

export interface IconProps extends Omit<SVGProps<SVGSVGElement>, 'name'> {
  name: IconName;
  /** Rendered pixel size. Stroke stays 1.5 at every size so the set holds together. */
  size?: number;
  /** Supply only when the icon carries meaning on its own. Otherwise it stays hidden. */
  title?: string;
}

export function Icon({ name, size = 20, title, className, ...rest }: IconProps) {
  return (
    <svg
      viewBox="0 0 20 20"
      width={size}
      height={size}
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      strokeLinecap="square"
      strokeLinejoin="miter"
      className={cx('shrink-0', className)}
      role={title ? 'img' : undefined}
      aria-hidden={title ? undefined : true}
      aria-label={title}
      {...rest}
    >
      {title ? <title>{title}</title> : null}
      {paths[name].split(' M').map((segment, i) => (
        <path key={i} d={i === 0 ? segment : `M${segment}`} />
      ))}
    </svg>
  );
}
